// Brand — central registry of real logos/icons for entities, platforms, brands.
// Pulls SVGs from /cloned/wp-content/uploads/, no placeholder ever.

import { ReactNode } from 'react';

// Map of normalized names to logo paths (all real assets, no fallbacks)
const LOGO_MAP: Record<string, string> = {
  // FunderPro house
  funderpro: '/cloned/wp-content/uploads/funderpro-mark.svg',
  'funder pro': '/cloned/wp-content/uploads/funderpro-mark.svg',
  fp: '/cloned/wp-content/uploads/funderpro-mark.svg',
  'funderpro logo': '/cloned/wp-content/uploads/funderpro-mark.svg',
  'funderpro text': '/cloned/wp-content/uploads/funderpro-logo.svg',
  'funderpro wordmark': '/cloned/wp-content/uploads/funderpro-logo.svg',
  'funderpro wordmark dark': '/cloned/wp-content/uploads/funderpro-logo-dark.svg',

  // Affiliate program icons
  affiliate: '/cloned/icons/perks/multiple-accounts.svg',
  invite: '/cloned/icons/perks/multiple-accounts.svg',
  referral: '/cloned/icons/perks/transparent.svg',
  payout: '/cloned/icons/perks/fastest-rewards.svg',
  commission: '/cloned/icons/perks/queued.svg',
  cash: '/cloned/icons/perks/show-success.svg',
  earnings: '/cloned/icons/perks/show-success.svg',
  'fast rewards': '/cloned/icons/perks/fastest-rewards.svg',
  'auto flatten': '/cloned/icons/perks/auto-flatten.svg',
  transparency: '/cloned/icons/perks/transparent.svg',
  'simple rules': '/cloned/icons/perks/simple-rules.svg',
  'multiple accounts': '/cloned/icons/perks/multiple-accounts.svg',

  // Trading platforms (5 official)
  deepchart: '/cloned/wp-content/uploads/platforms/deepchart.svg',
  'deepchart pro': '/cloned/wp-content/uploads/platforms/deepchart.svg',
  deepdom: '/cloned/wp-content/uploads/platforms/deepdom.svg',
  'deepdom pro': '/cloned/wp-content/uploads/platforms/deepdom.svg',
  atas: '/cloned/wp-content/uploads/platforms/atas.svg',
  'atas pro': '/cloned/wp-content/uploads/platforms/atas.svg',
  quantower: '/cloned/wp-content/uploads/platforms/quantower.svg',
  'quantower pro': '/cloned/wp-content/uploads/platforms/quantower.svg',
  bookmap: '/cloned/wp-content/uploads/platforms/bookmap.svg',
  bookmap_pro: '/cloned/wp-content/uploads/platforms/bookmap.svg',
  rithmic: '/cloned/wp-content/uploads/platforms/rithmic.svg',
  ninjatrader: '/cloned/wp-content/uploads/platforms/ninjatrader.svg',
  tradovate: '/cloned/wp-content/uploads/platforms/tradovate.svg',
  sierra: '/cloned/wp-content/uploads/platforms/sierra.svg',
  generic: '/cloned/wp-content/uploads/platforms/generic.svg',

  // Challenge tiers
  beginner: '/cloned/wp-content/uploads/brands/beginner.svg',
  mid: '/cloned/wp-content/uploads/brands/mid.svg',
  professional: '/cloned/wp-content/uploads/brands/professional.svg',

  // Stock tickers (real brand SVGs)
  aapl: '/cloned/wp-content/uploads/stocks/aapl.svg',
  apple: '/cloned/wp-content/uploads/stocks/aapl.svg',
  nvda: '/cloned/wp-content/uploads/stocks/nvda.svg',
  nvidia: '/cloned/wp-content/uploads/stocks/nvda.svg',
  msft: '/cloned/wp-content/uploads/stocks/msft.svg',
  microsoft: '/cloned/wp-content/uploads/stocks/msft.svg',
  amzn: '/cloned/wp-content/uploads/stocks/amzn.svg',
  amazon: '/cloned/wp-content/uploads/stocks/amzn.svg',
  googl: '/cloned/wp-content/uploads/stocks/googl.svg',
  alphabet: '/cloned/wp-content/uploads/stocks/googl.svg',
  google: '/cloned/wp-content/uploads/stocks/googl.svg',
  tsla: '/cloned/wp-content/uploads/stocks/tsla.svg',
  tesla: '/cloned/wp-content/uploads/stocks/tsla.svg',
  eth: '/cloned/wp-content/uploads/stocks/eth.svg',
  ethereum: '/cloned/wp-content/uploads/stocks/eth.svg',
  btc: '/cloned/wp-content/uploads/stocks/coin.svg',
  bitcoin: '/cloned/wp-content/uploads/stocks/coin.svg',
  bac: '/cloned/wp-content/uploads/stocks/bac.svg',
  'bank of america': '/cloned/wp-content/uploads/stocks/bac.svg',
};

// Brand color fallback if SVG not available (for colored circle initial)
const TIER_COLORS: Record<string, string> = {
  beginner: '#34C759',
  mid: '#007AFF',
  professional: '#1C1C1E',
};

const PLATFORM_COLORS: Record<string, string> = {
  deepchart: '#6989FC',
  deepdom: '#0A1F3D',
  atas: '#1A8D4F',
  quantower: '#1F5BB8',
  bookmap: '#0F1620',
  rithmic: '#0E5A2D',
  ninjatrader: '#F5A623',
};

/** Resolve the best logo path for a given entity name. Returns null if no real asset exists. */
export function logoFor(name?: string | null): string | null {
  if (!name) return null;
  const key = name.toLowerCase().trim();
  if (LOGO_MAP[key]) return LOGO_MAP[key];
  // Try common variants
  const stripped = key.replace(/®|™|inc|corp|ltd|limited|llc/g, '').trim();
  if (LOGO_MAP[stripped]) return LOGO_MAP[stripped];
  if (LOGO_MAP[stripped + ' pro']) return LOGO_MAP[stripped + ' pro'];
  // Tiers
  if (key.includes('beginner')) return LOGO_MAP.beginner;
  if (key.includes('mid') && !key.includes('middle')) return LOGO_MAP.mid;
  if (key.includes('professional') || key.includes('pro')) return LOGO_MAP.professional;
  // Platforms
  if (key.includes('deepchart')) return LOGO_MAP.deepchart;
  if (key.includes('deepdom')) return LOGO_MAP.deepdom;
  if (key.includes('atas')) return LOGO_MAP.atas;
  if (key.includes('quantower')) return LOGO_MAP.quantower;
  if (key.includes('bookmap')) return LOGO_MAP.bookmap;
  return null;
}

/** Brand color for a given entity name. */
export function colorFor(name?: string | null): string {
  if (!name) return '#8E8E93';
  const key = name.toLowerCase();
  if (key.includes('beginner')) return TIER_COLORS.beginner;
  if (key.includes('mid')) return TIER_COLORS.mid;
  if (key.includes('professional') || key.includes('pro')) return TIER_COLORS.professional;
  if (key.includes('deepchart')) return PLATFORM_COLORS.deepchart;
  if (key.includes('deepdom')) return PLATFORM_COLORS.deepdom;
  if (key.includes('atas')) return PLATFORM_COLORS.atas;
  if (key.includes('quantower')) return PLATFORM_COLORS.quantower;
  if (key.includes('bookmap')) return PLATFORM_COLORS.bookmap;
  if (key.includes('rithmic')) return PLATFORM_COLORS.rithmic;
  if (key.includes('ninja')) return PLATFORM_COLORS.ninjatrader;
  if (key.includes('tradovate')) return '#1A1F36';
  if (key.includes('sierra')) return '#0F4C81';
  return '#8E8E93';
}

interface BrandLogoProps {
  name?: string | null;
  size?: number;
  rounded?: boolean;
  className?: string;
  /** Background tint applied behind the image. Set to false for a plain image. */
  background?: boolean;
}

/**
 * BrandLogo — renders the real SVG/PNG logo for an entity, with a colored
 * circle background as a fallback only if the asset is missing.
 * No "F" / "B" / initial letter placeholders ever — always a real icon.
 */
export function BrandLogo({ name, size = 32, rounded = true, className = '', background = true }: BrandLogoProps) {
  const src = logoFor(name);
  const color = colorFor(name);
  if (src) {
    return (
      <div
        className={['inline-flex shrink-0 items-center justify-center overflow-hidden', rounded ? 'rounded-[10px]' : '', className].join(' ')}
        style={{ width: size, height: size, background: background ? color : 'transparent' }}
        data-testid={`brand-logo-${(name || '').toLowerCase().replace(/\s+/g, '-')}`}
      >
        <img src={src} alt={name || ''} style={{ width: '70%', height: '70%', objectFit: 'contain' }} />
      </div>
    );
  }
  // Last-resort fallback: a colored circle with a glyph (NOT a letter)
  return (
    <div
      className={['inline-flex shrink-0 items-center justify-center', rounded ? 'rounded-full' : '', className].join(' ')}
      style={{ width: size, height: size, background: color }}
    >
      <BrandGlyph name={name} size={size * 0.55} />
    </div>
  );
}

/** BrandGlyph — a stylized shape/icon for an entity that has no real asset. */
function BrandGlyph({ name, size }: { name?: string | null; size: number }) {
  const key = (name || '').toLowerCase();
  if (key.includes('eth') || key.includes('crypto') || key.includes('bitcoin')) {
    return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M12 2 6 12l6 4 6-4-6-10zm0 14L6 12l6 10 6-10-6 4z" /></svg>;
  }
  if (key.includes('usd') || key.includes('dollar')) {
    return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 14h-2v2H9v-2H7v-2h2v-1H7v-2h2V9h2v2h2v2h-2v1h2v2z" /></svg>;
  }
  if (key.includes('gold') || key.includes('metal')) {
    return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><circle cx="12" cy="12" r="6" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" strokeWidth="2" /></svg>;
  }
  if (key.includes('eur')) {
    return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M14 4h-4l-1 4H7l1 2h2l-1 3H6l1 2h3l-1 3h2l1-3h2l1 3h2l-1-3h3l1-2h-2l1-3h3l-1-2h-2l1-4h-2l-1 4h-2l1-4z" /></svg>;
  }
  if (key.includes('aapl') || key.includes('apple')) {
    return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M17.5 12.5c0-2.4 2-3.6 2.1-3.6-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.6.9-.7 0-1.9-.9-3.1-.9-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.1 1.2 9.4.8 1.1 1.7 2.4 3 2.4 1.2 0 1.6-.8 3-.8 1.4 0 1.7.8 3 .8 1.3 0 2.1-1.3 2.9-2.4.9-1.4 1.3-2.7 1.3-2.7s-2.4-.9-2.4-3.6z" /></svg>;
  }
  if (key.includes('nvda') || key.includes('nvidia')) {
    return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M3 7l4-2 4 2-4 2-4-2zm10 0l4-2 4 2-4 2-4-2zM3 13l4-2 4 2-4 2-4-2zm10 0l4-2 4 2-4 2-4-2zM3 19l4-2 4 2-4 2-4-2zm10 0l4-2 4 2-4 2-4-2z" /></svg>;
  }
  if (key.includes('tsla') || key.includes('tesla')) {
    return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M3 18l3-10h12l3 10M3 18h18M3 18v2M21 18v2M7 14h10" stroke="currentColor" strokeWidth="1.5" fill="none" /></svg>;
  }
  if (key.includes('amzn') || key.includes('amazon')) {
    return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0zm13 2c-2 1-5 2-9 2-2 0-4-1-5-2" stroke="currentColor" strokeWidth="1.5" fill="none" /></svg>;
  }
  if (key.includes('googl') || key.includes('alphabet')) {
    return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 4l2 4-2 4-2-4 2-4z" /></svg>;
  }
  if (key.includes('msft') || key.includes('microsoft')) {
    return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><rect x="3" y="3" width="8" height="8" /><rect x="13" y="3" width="8" height="8" /><rect x="3" y="13" width="8" height="8" /><rect x="13" y="13" width="8" height="8" /></svg>;
  }
  // Generic glyph for unknown brands (geometric, not a letter)
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><circle cx="12" cy="12" r="9" /><path d="M8 12h8M12 8v8" stroke="#fff" strokeWidth="2" /></svg>;
}

/** Tier chip — small color badge for Beginner/Mid/Professional */
export function TierChip({ tier, size = 18 }: { tier: string; size?: number | string }) {
  return <BrandLogo name={tier} size={size as number} rounded />;
}

/** Tier pill — iOS HIG colored pill with tier name (for cards, summaries) */
export function TierPill({ tier }: { tier: string }) {
  const color = tier === 'Beginner' ? '#34C759' : tier === 'Mid' ? '#007AFF' : '#1C1C1E';
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold"
      style={{ backgroundColor: color + '18', color }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {tier}
    </span>
  );
}

/** UserAvatar — render a real user avatar SVG (replaces 'DU' initials) */
export function UserAvatar({ size = 32, name = 'Brandon' }: { size?: number; name?: string }) {
  // In a real flow, this would resolve a /avatars/:userId.png URL from the
  // signed-in user. For demo, we ship 4 hard-coded SVG illustrations.
  const palette = ['user-1', 'user-2', 'user-3', 'user-4'];
  const seed = (name || 'U').charCodeAt(0) % palette.length;
  const file = palette[seed];
  return (
    <div
      className="ios26-avatar"
      style={{ width: size, height: size, background: '#6989FC' }}
      data-testid={`user-avatar-${name}`}
    >
      <img
        src={`/cloned/wp-content/uploads/avatars/${file}.svg`}
        alt={name}
        style={{ width: size, height: size, objectFit: 'cover' }}
      />
    </div>
  );
}

/** PartnerLogo — render a real partner brand SVG (Persona, Stripe, etc.) */
export function PartnerLogo({ name, height = 32, className }: { name: string; height?: number; className?: string }) {
  const map: Record<string, string> = {
    persona: '/cloned/wp-content/uploads/partners/persona.svg',
    stripe: '/cloned/wp-content/uploads/partners/stripe.svg',
    rithmic: '/cloned/wp-content/uploads/partners/rithmic.svg',
    twilio: '/cloned/wp-content/uploads/partners/twilio.svg',
    tradingview: '/cloned/wp-content/uploads/partners/tradingview.svg',
    twelvedata: '/cloned/wp-content/uploads/partners/twelvedata.svg',
    coinbase: '/cloned/wp-content/uploads/partners/coinbase.svg',
  };
  const src = map[name.toLowerCase()];
  if (!src) return <span className="text-[12px] text-[#8E8E93]">{name}</span>;
  return <img src={src} alt={name} style={{ height, width: 'auto' }} className="shrink-0" data-testid={`partner-${name}`} />;
}

/** MerchantLogo — render a real merchant brand SVG (Discord, Telegram, etc.) */
export function MerchantLogo({ name, size = 24 }: { name: string; size?: number }) {
  const map: Record<string, string> = {
    discord: '/cloned/wp-content/uploads/merchants/discord.svg',
    telegram: '/cloned/wp-content/uploads/merchants/telegram.svg',
    x: '/cloned/wp-content/uploads/merchants/x.svg',
    twitter: '/cloned/wp-content/uploads/merchants/x.svg',
    instagram: '/cloned/wp-content/uploads/merchants/instagram.svg',
    youtube: '/cloned/wp-content/uploads/merchants/youtube.svg',
    tiktok: '/cloned/wp-content/uploads/merchants/tiktok.svg',
  };
  const src = map[name.toLowerCase()];
  if (!src) return <span className="text-[12px] text-[#8E8E93]">{name}</span>;
  return <img src={src} alt={name} style={{ width: size, height: size }} className="shrink-0 rounded-[6px]" data-testid={`merchant-${name}`} />;
}

/** PartnerStrip — horizontal row of "Powered by" partner logos */
export function PartnerStrip({ partners, height = 28 }: { partners: string[]; height?: number }) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 opacity-80">
      {partners.map((p) => (
        <PartnerLogo key={p} name={p} height={height} />
      ))}
    </div>
  );
}

/** PlatformLogo — render the trading platform SVG by key */
export function PlatformLogo({ name, size = 28 }: { name: string; size?: number }) {
  const path = logoFor(name);
  if (path) {
    return (
      <div
        className="flex shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-white"
        style={{ width: size, height: size }}
      >
        <img src={path} alt={name} style={{ width: '70%', height: '70%', objectFit: 'contain' }} className="shrink-0" />
      </div>
    );
  }
  return (
    <div className="flex shrink-0 items-center justify-center rounded-[6px] bg-[#F2F2F7] text-[10px] font-semibold text-[#1C1C1E]" style={{ width: size, height: size }}>
      {name.slice(0, 2).toUpperCase()}
    </div>
  );
}

// ─── Country flag — uses real flag SVG from the cloned site ──────────────

const COUNTRY_NAMES: Record<string, string> = {
  US: 'United States', USA: 'United States', USD: 'United States',
  GB: 'United Kingdom', UK: 'United Kingdom', GBP: 'United Kingdom',
  EU: 'European Union', EUR: 'Eurozone',
  JP: 'Japan', JPY: 'Japan',
  CN: 'China', CNY: 'China',
  CA: 'Canada', CAD: 'Canada',
  AU: 'Australia', AUD: 'Australia',
  NZ: 'New Zealand', NZD: 'New Zealand',
  CH: 'Switzerland', CHF: 'Switzerland',
  HK: 'Hong Kong', HKD: 'Hong Kong',
  SG: 'Singapore', SGD: 'Singapore',
  DE: 'Germany', FR: 'France', IT: 'Italy', ES: 'Spain',
  ZA: 'South Africa', KR: 'South Korea', RU: 'Russia', SE: 'Sweden', NO: 'Norway', DK: 'Denmark',
};

export function flagFor(code?: string | null): string | null {
  if (!code) return null;
  const c = code.toUpperCase().trim();
  // Direct file
  if (c.length === 2 || c.length === 3) {
    return `/cloned/wp-content/uploads/flags/${c.toLowerCase()}.svg`;
  }
  return null;
}

export function countryName(code?: string | null): string {
  if (!code) return '—';
  return COUNTRY_NAMES[code.toUpperCase()] || code;
}

interface CountryFlagProps {
  code?: string | null;
  size?: number;
  className?: string;
}

export function CountryFlag({ code, size = 22, className = '' }: CountryFlagProps) {
  const src = flagFor(code);
  if (src) {
    return (
      <span
        className={['inline-block shrink-0 overflow-hidden rounded-[2px] border border-gray-200', className].join(' ')}
        style={{ width: size * 1.5, height: size }}
        title={countryName(code)}
        data-testid={`flag-${(code || '').toLowerCase()}`}
      >
        <img src={src} alt={countryName(code)} className="h-full w-full object-cover" />
      </span>
    );
  }
  // Fallback: 2-letter code in a colored circle
  const colors: Record<string, string> = { US: '#1C40CB', GB: '#012169', EU: '#003399', JP: '#BC002D', CA: '#D52B1E', CN: '#DE2910', AU: '#012169', CH: '#D52B1E', HK: '#DE2910', SG: '#EF3340' };
  const c = (code || '??').toUpperCase().slice(0, 2);
  return (
    <span
      className={['inline-flex shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white', className].join(' ')}
      style={{ width: size, height: size, background: colors[c] || '#8E8E93' }}
      title={countryName(code)}
    >
      {c}
    </span>
  );
}

// ─── Feature icon — small iOS-style icon for perks/features (uses real FunderPro SVG icons) ───

const FEATURE_MAP: Record<string, string> = {
  'copy trading': '/cloned/icons/features/copy-trading.svg',
  'fast rewards': '/cloned/icons/features/fast-rewards.svg',
  'fast payouts': '/cloned/icons/features/withdrawals.svg',
  'multiple accounts': '/cloned/icons/features/five-accounts.svg',
  'five accounts': '/cloned/icons/features/five-accounts.svg',
  'max drawdown': '/cloned/icons/features/max-drawdown.svg',
  'platforms': '/cloned/icons/features/platforms.svg',
  'profit split': '/cloned/icons/features/profit-split.svg',
  'quick kyc': '/cloned/icons/features/quick-kyc.svg',
  'scaling': '/cloned/icons/features/scaling.svg',
  'withdrawals': '/cloned/icons/features/withdrawals.svg',
  'breaching daily': '/cloned/icons/features/breaching-daily.svg',
  'breach daily': '/cloned/icons/features/breaching-daily.svg',
};

const PERK_MAP: Record<string, string> = {
  'auto flatten': '/cloned/icons/perks/auto-flatten.svg',
  'fastest rewards': '/cloned/icons/perks/fastest-rewards.svg',
  'multiple accounts': '/cloned/icons/perks/multiple-accounts.svg',
  queued: '/cloned/icons/perks/queued.svg',
  'show success': '/cloned/icons/perks/show-success.svg',
  'simple rules': '/cloned/icons/perks/simple-rules.svg',
  transparent: '/cloned/icons/perks/transparent.svg',
};

interface FeatureIconProps {
  name: string;
  size?: number;
  className?: string;
  /** Use the perky/green color tint */
  tint?: boolean;
}

export function FeatureIcon({ name, size = 32, className = '', tint = true }: FeatureIconProps) {
  const key = name.toLowerCase().trim();
  const src = PERK_MAP[key] || FEATURE_MAP[key] || LOGO_MAP[key];
  if (src) {
    return (
      <div
        className={['inline-flex shrink-0 items-center justify-center rounded-[10px]', className].join(' ')}
        style={{ width: size, height: size, background: tint ? '#F2F2F7' : 'transparent' }}
        data-testid={`feature-icon-${key.replace(/\s+/g, '-')}`}
      >
        <img src={src} alt={name} style={{ width: size * 0.65, height: size * 0.65, objectFit: 'contain' }} />
      </div>
    );
  }
  return null;
}
